import React from 'react';
import {
  HStack,
  Input,
  NewBasicButton,
  NewTextButton,
  RadioButton,
  Text,
  VStack,
} from '@design-system/mobile';
import { isEmpty } from 'lodash-es';
import {
  Address,
  QuickConsultInfoFromUntact,
  ConsultUserBasicInfo,
} from '@remodeling/remodeling-manager';
import SearchZoneCode from '@/components/SearchZoneCode';
import useBasicInfo from './hooks/useBasicInfo';
import {
  getFieldErrorMessage,
  getFieldErrorMessages,
  hasFieldError,
  hasFieldErrors,
} from '@/utils';
import { CONSULT_NAME_PATTERN } from '@/constants/consult';
import TrackingService from '@/services/tracking';

interface BasicInfoProps {
  userInfo?: ConsultUserBasicInfo;
  isUntact?: boolean;
  handleChangeAddressInfo?: (value: Address, isDefaultAddress: boolean) => void;
  disabledAll?: boolean;
  untactInfo?: QuickConsultInfoFromUntact;
  workAvailableAreas?: string[] | [];
  handleOpenBottomSheet?: () => void;
}

const BasicInfo = ({
  userInfo,
  isUntact,
  handleChangeAddressInfo,
  disabledAll,
  untactInfo,
  workAvailableAreas = [],
  handleOpenBottomSheet,
}: BasicInfoProps) => {
  const {
    isDefaultAddress,
    isZoneCode,
    handleClickDefaultAddress,
    handleClickDirectInput,
    handleClickSearchZoneCode,
    handleChangeZoneCode,
    formState: { errors },
    register,
    isAuthenticated,
    handleChangeTelInput,
    handleChangeSiteData,
    isNameDisabled,
    isTelDisabled,
    handleChangeDetailAddress,
    handleChangeName,
  } = useBasicInfo({
    userInfo,
    handleChangeAddressInfo,
    isUntact,
    untactInfo,
  });

  return (
    <>
      {!isUntact && (
        <>
          <Text
            variant="label_xlg_m"
            color="color_chip.gray.700"
            mt="8px"
            mb={12}
          >
            이름
          </Text>
          <Input
            size="lg"
            placeholder="이름 입력"
            disabled={disabledAll || isNameDisabled}
            {...register('name', {
              required: {
                value: true,
                message: '이름을 정확히 입력해 주세요.',
              },
              pattern: {
                value: new RegExp(CONSULT_NAME_PATTERN),
                message: '이름을 정확히 입력해 주세요.',
              },
              onChange: (e) => handleChangeName(e),
            })}
            mb={36}
            isError={hasFieldError(errors, 'name')}
            errorMessage={getFieldErrorMessage(errors, 'name')}
          />
          <Text variant="label_xlg_m" color="color_chip.gray.700" mb={12}>
            휴대폰번호
          </Text>
          <Input
            size="lg"
            placeholder="숫자만 입력"
            disabled={disabledAll || isTelDisabled}
            type="string"
            maxLength={13}
            {...register('tel', {
              required: {
                value: true,
                message: '휴대폰번호를 정확히 입력해 주세요.',
              },
              pattern: {
                value: /^\d{3}-\d{3,4}-\d{4}$/,
                message: '휴대폰번호를 정확히 입력해 주세요.',
              },
            })}
            mb={36}
            isError={hasFieldError(errors, 'tel')}
            errorMessage={getFieldErrorMessage(errors, 'tel')}
            onChange={handleChangeTelInput}
          />
        </>
      )}

      <Text
        variant="label_xlg_m"
        color="color_chip.gray.700"
        mb={!isEmpty(workAvailableAreas) ? '8px' : 12}
      >
        시공주소
      </Text>
      {!isEmpty(workAvailableAreas) && (
        <HStack mb={12}>
          <NewTextButton
            size="lg"
            isUnderline
            color="color_chip.blue.500"
            type="button"
            onClick={handleOpenBottomSheet}
          >
            상담 가능 지역
          </NewTextButton>
          <Text variant="body2" color="color_chip.gray.800" ml="4px">
            외 신청시 담당자가 변경될 수 있습니다
          </Text>
        </HStack>
      )}
      {isAuthenticated && userInfo?.site?.address?.zoneCode && (
        <HStack space={24} mb={12}>
          <RadioButton
            labelOptions={{
              text: '기본배송지',
              textVariant: 'label_lg_r',
            }}
            disabled={disabledAll}
            name="default_address"
            onClick={handleClickDefaultAddress}
            checked={isDefaultAddress}
            value="defaultAddress"
            className="consult-basic-info-default-address"
          />
          <RadioButton
            labelOptions={{
              text: '직접입력',
              textVariant: 'label_lg_r',
            }}
            name="default_address"
            disabled={disabledAll}
            onClick={handleClickDirectInput}
            checked={!isDefaultAddress}
            value="directInput"
            className="consult-basic-info-direct-address"
          />
        </HStack>
      )}
      <VStack space={8} mb={20}>
        <HStack space={7} width="100%" alignment="start">
          <Input
            size="lg"
            width="100%"
            placeholder="우편번호"
            {...register('site.address.zoneCode', {
              required: {
                value: true,
                message: '우편번호를 입력해주세요',
              },
            })}
            isError={hasFieldError(errors, 'site.address.zoneCode')}
            errorMessage={getFieldErrorMessage(errors, 'site.address.zoneCode')}
            onChange={handleChangeZoneCode}
            readOnly={true}
            disabled={disabledAll || isZoneCode}
          />
          <NewBasicButton
            size="lg"
            emphasis="low"
            type="button"
            onClick={handleClickSearchZoneCode}
            width="133px"
            icon={{ variant: 'ic_search', align: 'left' }}
            disabled={disabledAll || isDefaultAddress}
            {...TrackingService.tagGTM('search_zone_code_button', {
              'data-gtm-tracking-consult-type': isUntact ? 'untact' : 'quick',
            })}
          >
            우편번호 검색
          </NewBasicButton>
        </HStack>
        <Input
          size="lg"
          width="100%"
          placeholder="주소입력"
          {...register('site.address.roadnameAddress', {
            required: {
              value: true,
              message: '주소를 입력해주세요',
            },
          })}
          isError={hasFieldErrors(errors, [
            'site.address.roadnameAddress',
            'site.address.sidoCode',
          ])}
          errorMessage={getFieldErrorMessages(errors, [
            'site.address.roadnameAddress',
            'site.address.sidoCode',
          ])}
          readOnly={true}
          disabled={disabledAll || isZoneCode}
        />
        <Input
          size="lg"
          width="100%"
          placeholder="상세주소"
          {...register('site.addressDetail', {
            required: {
              value: true,
              message: '주소를 정확히 입력해 주세요.',
            },
            pattern: {
              value: /^[^ㄱ-ㅎㅏ-ㅣ]+$/g,
              message: '주소를 정확히 입력해 주세요.',
            },
            onChange: (e) => handleChangeDetailAddress(e),
          })}
          isError={hasFieldError(errors, 'site.addressDetail')}
          maxLength={50}
          errorMessage={getFieldErrorMessage(errors, 'site.addressDetail')}
          readOnly={!isZoneCode}
          disabled={disabledAll || isDefaultAddress}
        />
        <input
          type="hidden"
          {...register('site.address.sidoCode', {
            required: {
              value: true,
              message: '주소를 정확히 입력해 주세요.',
            },
          })}
        />
        <SearchZoneCode handleChangeSiteData={handleChangeSiteData} />
      </VStack>
    </>
  );
};

export default BasicInfo;
